// Quota / paywall API.
//
// Backed by writeon-django's ``/api/quota/*`` endpoints. The flow:
//
//   1. Frontend AI wrappers (grammar, image-gen, etc.) call
//      ``consumeQuota(feature)`` BEFORE hitting the FastAPI service.
//   2. If the response says ``allowed: true``, the call goes through.
//   3. If the server returns 429 (allowed=false, reason=limit_reached),
//      the wrapper short-circuits — no FastAPI call burned — and
//      ``onPaywall`` fires so the UI can show the upsell toast.
//
// The catalogue endpoint is public; the status + consume endpoints
// require auth.

import { apiClient } from './apiClient';
import { logger } from './logger';

export const QUOTA_FEATURES = [
  'ai_grammar',
  'ai_inspiration',
  'ai_autocomplete',
  'ai_image',
  'ai_planner_text',
  'ai_coherence',
  'ai_project_critique',
  'ai_tts',
  'ai_tts_download',
  'dictionary',
];

export const QUOTA_TIERS = ['free', 'basic', 'premium'];

/**
 * Fetch today's usage + per-feature limits + current tier.
 * Returns { tier, day_key, features: { <feature>: { used, limit } } }.
 */
export async function fetchQuotaStatus() {
  try {
    const { data } = await apiClient.get('/api/quota/status/');
    return data;
  } catch (err) {
    logger.warn('quota_status_unavailable', err?.message);
    return null;
  }
}

/**
 * Public catalogue — every (feature × tier) limit. Used by the
 * pricing page so the numbers stay in sync with the backend.
 */
export async function fetchQuotaCatalogue() {
  try {
    const { data } = await apiClient.get('/api/quota/catalogue/');
    return data;
  } catch (err) {
    logger.warn('quota_catalogue_unavailable', err?.message);
    return null;
  }
}

/**
 * Atomic increment. Returns the parsed verdict whether allowed or
 * not — the caller branches on ``verdict.allowed``. A 429 surfaces
 * as ``{ allowed: false, reason: 'limit_reached', ... }`` so the
 * caller can read used/limit to render an upsell with concrete
 * numbers ("you've used 30/30 of today's free grammar checks").
 */
export async function consumeQuota(feature) {
  if (!QUOTA_FEATURES.includes(feature)) {
    return { allowed: false, reason: 'unknown_feature', feature };
  }
  try {
    const { data } = await apiClient.post(
      `/api/quota/${feature}/consume/`,
      {},
    );
    return data;
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (status === 429 && data) {
      return data;
    }
    // Network / unknown errors: don't block the user — log + allow.
    // The server-side cap still applies if our request never reached
    // the upstream service; this only matters when the quota
    // endpoint itself is temporarily unreachable.
    logger.warn('quota_consume_error', { feature, message: err?.message });
    return { allowed: true, feature, reason: 'transport_error' };
  }
}

/**
 * Convenience: run ``apiCall`` only if the quota allows it. Returns
 * either the API result or ``{ paywalled: true, verdict }``. Lets a
 * caller drop in:
 *
 *   const out = await withQuota('ai_grammar', () => grammarApi.check(text));
 *   if (out?.paywalled) showUpsell(out.verdict); else useResult(out);
 */
export async function withQuota(feature, apiCall) {
  const verdict = await consumeQuota(feature);
  if (!verdict.allowed) {
    return { paywalled: true, verdict };
  }
  return apiCall();
}
